import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

interface Testimonial {
  id: number;
  name: string;
  avatar: string;
  rating: number;
  text: string;
  tour: string;
  date: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Александр Петров',
    avatar: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150',
    rating: 5,
    text: 'Огромное спасибо Hasan Travel за отличную организацию нашего отпуска в Турции! Все было на высшем уровне: от встречи в аэропорту до размещения в отеле. Особая благодарность менеджеру Алине за внимательное отношение и помощь в выборе тура.',
    tour: 'Турция, Белек',
    date: 'Май 2024'
  },
  {
    id: 2,
    name: 'Елена Смирнова',
    avatar: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=150',
    rating: 5,
    text: 'Второй раз пользуемся услугами Hasan Travel и снова остались довольны! Отдых в Египте превзошел все ожидания. Отель соответствовал описанию, питание отличное, море чистое. Будем обращаться еще и рекомендовать друзьям!',
    tour: 'Египет, Хургада',
    date: 'Октябрь 2024'
  },
  {
    id: 3,
    name: 'Дмитрий Ковалев',
    avatar: 'https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=150',
    rating: 5,
    text: 'Хорошая турфирма с профессиональными сотрудниками. Оперативно подобрали нам тур в ОАЭ по приемлемой цене. Всё прошло отлично, рекомендуем!',
    tour: 'ОАЭ, Дубай',
    date: 'Февраль 2025'
  },
  {
    id: 4,
    name: 'Ольга Ким',
    avatar: 'https://images.pexels.com/photos/1065084/pexels-photo-1065084.jpeg?auto=compress&cs=tinysrgb&w=150',
    rating: 5,
    text: 'Выражаю благодарность команде Hasan Travel за организацию нашего семейного отдыха в Таиланде! Все было продумано до мелочей: от перелета до экскурсионной программы. Дети были в восторге! Обязательно обратимся еще раз.',
    tour: 'Таиланд, Пхукет',
    date: 'Январь 2025'
  }
];

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(2);

  useEffect(() => {
    const updateItemsPerPage = () => {
      setItemsPerPage(window.innerWidth >= 768 ? 2 : 1);
    };
    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) =>
      prev + itemsPerPage >= testimonials.length ? 0 : prev + itemsPerPage
    );
  }, [itemsPerPage]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) =>
      prev - itemsPerPage < 0
        ? testimonials.length - (testimonials.length % itemsPerPage || itemsPerPage)
        : prev - itemsPerPage
    );
  }, [itemsPerPage]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 8000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <section className="py-20 bg-white">
      <div className="container-custom">
        <div className="text-center mb-14">
          <div className="inline-block bg-primary-500/10 text-primary-500 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            Отзывы
          </div>
          <h2 className="section-title mx-auto">Что говорят наши клиенты</h2>
          <p className="text-neutral-500 mt-4 max-w-xl mx-auto">
            Более 10 000 довольных туристов доверили нам организацию своего отдыха
          </p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {visibleTestimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-neutral-50 rounded-2xl p-8 relative border border-neutral-100 hover:shadow-lg transition-shadow duration-300"
              >
                <Quote className="absolute top-6 right-6 w-10 h-10 text-primary-500/10" />
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover ring-4 ring-white shadow-md mr-4"
                  />
                  <div>
                    <h3 className="font-bold text-neutral-800 text-lg">
                      {testimonial.name}
                    </h3>
                    <div className="flex mt-1 gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-neutral-600 leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex justify-between items-center pt-4 border-t border-neutral-200">
                  <span className="text-sm font-medium text-primary-500">{testimonial.tour}</span>
                  <span className="text-sm text-neutral-400">{testimonial.date}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <button
            onClick={prevSlide}
            className="absolute -left-4 lg:-left-14 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center text-primary-500 hover:bg-primary-500 hover:text-white transition-all duration-300"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute -right-4 lg:-right-14 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center text-primary-500 hover:bg-primary-500 hover:text-white transition-all duration-300"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center mt-10 gap-2">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i * itemsPerPage)}
              className={`h-2 rounded-full transition-all duration-500 ${
                i === Math.floor(currentIndex / itemsPerPage)
                  ? 'bg-primary-500 w-10'
                  : 'bg-neutral-300 w-4 hover:bg-neutral-400'
              }`}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
